import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";

import { useCustomerDetailsForInsurance } from "../../CustomHooks/Queries";
import { useSaveInsuranceDetails } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";

import { dispatchAPIErrorToast } from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";
import CloseButton from "../../Components/Buttons/close-button";
import PrimaryButton from "../../Components/Buttons/primary-button";
import CheckBox from "../../Components/Form Fields/checkbox";

import "./insurance.scss";

export default function ApplyInsurance() {
  const queryClient = useQueryClient();

  const { onModalClose } = useModalContext();

  const [acknowledged, setAcknowledged] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);

  const { data: insuranceData, isLoading: isLoadingInsuranceData } = useCustomerDetailsForInsurance();

  const { mutate: saveInsuranceDetails } = useSaveInsuranceDetails({
    onSuccess: () => {
      queryClient.refetchQueries(APIQueryKeys.consumerDeals);
      onModalClose();
    },
    onError: (error) =>
      dispatchAPIErrorToast({
        message: error?.response?.data?.error,
        key: "saveInsuranceDetails",
      }),
  });

  // Build insurance URL with pre-filled parameters from API
  const insuranceUrl = useMemo(() => {
    const baseUrl = process.env.REACT_APP_SURE_BASE_URL;
    const params = new URLSearchParams();

    const customerInfo = insuranceData?.customer_information ?? {};
    const interestedPartyInfo = insuranceData?.interested_party_information ?? {};

    // Contact Info
    if (customerInfo?.first_name) params.append("first_name", customerInfo.first_name);
    if (customerInfo?.last_name) params.append("last_name", customerInfo.last_name);
    if (customerInfo?.email) params.append("email", customerInfo.email);
    if (customerInfo?.phone_number) params.append("phone_number", customerInfo.phone_number);
    if (customerInfo?.street_address) params.append("street_address", customerInfo.street_address);
    if (customerInfo?.unit) params.append("unit", customerInfo.unit);
    if (customerInfo?.region) params.append("region", customerInfo.region);
    if (customerInfo?.city) params.append("city", customerInfo.city);
    if (customerInfo?.postal) params.append("postal", customerInfo.postal);

    // Interested Party Information (Property Information)
    if (interestedPartyInfo?.ip_first_name) params.append("ip_first_name", interestedPartyInfo.ip_first_name);
    if (interestedPartyInfo?.ip_line1) params.append("ip_line1", interestedPartyInfo.ip_line1);
    if (interestedPartyInfo?.ip_city) params.append("ip_city", interestedPartyInfo.ip_city);
    if (interestedPartyInfo?.ip_state) params.append("ip_state", interestedPartyInfo.ip_state);
    if (interestedPartyInfo?.ip_zip) params.append("ip_zip", interestedPartyInfo.ip_zip);

    // Add customID (mobile_id) for tracking
    if (customerInfo?.mobile_id) params.append("customID", customerInfo.mobile_id);

    const queryString = params.toString();
    return queryString ? `${baseUrl}&${queryString}` : baseUrl;
  }, [insuranceData]);

  // Listen for purchase success events from Sure iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Verify the message is from Sure's domain
      if (event.origin === "https://stake.trysureapp.com" || event.origin === "https://stake.sureapp.com") {
        const data = event.data;

        if (data?.actionType === "PURCHASE_RENTERS_SUCCESS") {
          // Save insurance details to database
          // customID from the payload will be used as mobile_user_id
          if (data?.customID) {
            saveInsuranceDetails(data);
          } else {
            console.error("Cannot save insurance details: customID not available in purchase event");
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [saveInsuranceDetails]);

  return (
    <div className="insurance-container">
      <div className="insurance-header">
        <StakeLogo className="stake-logo" />
        <CloseButton onClick={onModalClose} className="close" />
      </div>
      {isLoadingInsuranceData ? (
        <div className="loading-content">
          <CircularProgress />
        </div>
      ) : !hasConsented ? (
        <div className="insurance-consent-gate">
          <div className="d-flex align-items-start gap-2 mb-4">
            <CheckBox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              ariaLabelledBy="insurance-consent-label"
            />
            <p className="text-base-regular color--charcoal mb-0 px-2" id="insurance-consent-label">
              By selecting the checkbox, you consent to sharing your information with Sure App.
            </p>
          </div>
          <PrimaryButton disabled={!acknowledged} onClick={() => setHasConsented(true)}>
            Continue
          </PrimaryButton>
        </div>
      ) : (
        <iframe src={insuranceUrl} title="Renter's Insurance" className="insurance-iframe" />
      )}
    </div>
  );
}
